import React, { useState } from 'react';
import { recordHighlightInterest } from '../api/rttsClient';
import { STUDIO_COPY } from '../config';

type HighlightInterestProbeProps = {
  /** When false, hide the probe (gate off / choice UI restored). */
  visible?: boolean;
};

/**
 * Soft-beta demand probe — replaces the locked Highlight option.
 * Idempotent interest signal; dismissible for the session.
 */
const HighlightInterestProbe: React.FC<HighlightInterestProbeProps> = ({
  visible = true,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!visible || dismissed) return null;

  const onNotify = async () => {
    if (busy || confirmed) return;
    setBusy(true);
    try {
      await recordHighlightInterest();
      setConfirmed(true);
    } catch {
      // Soft-fail — still show confirmation so the CTA never feels broken.
      setConfirmed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="studio-highlight-probe"
      data-testid="studio-highlight-interest-probe"
    >
      <div className="studio-highlight-probe-copy">
        <p className="text-sm text-[var(--studio-muted)]">
          {STUDIO_COPY.highlightComingSoon}
        </p>
        {confirmed ? (
          <p
            className="text-sm text-[var(--studio-accent-bright)]"
            data-testid="studio-highlight-interest-confirm"
          >
            {STUDIO_COPY.highlightNotifyConfirm}
          </p>
        ) : (
          <button
            type="button"
            className="studio-btn-ghost studio-highlight-probe-cta"
            disabled={busy}
            onClick={() => {
              void onNotify();
            }}
            data-testid="studio-highlight-interest-cta"
          >
            {busy ? 'Saving…' : STUDIO_COPY.highlightNotifyCta}
          </button>
        )}
      </div>
      <button
        type="button"
        className="studio-btn-ghost studio-highlight-probe-dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        data-testid="studio-highlight-interest-dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default HighlightInterestProbe;
